




















































































































































































import {
  computed,
  defineComponent,
  onMounted,
  ref,
  toRefs,
  watch
} from '@nuxtjs/composition-api'
import FieldError from '~/components/car/classifieds/form/fieldsets/fields/FieldError.vue'
import { computeFieldState } from '~/utils/form'
import { useDep } from '~/compositions/dependency-container'
import FormValidationBuilder from '~/builders/form/FormValidationBuilder'
import { useClassifiedForm } from '~/compositions/form'
import { useI18n } from '~/compositions/i18n'
import { defineComponentTranslations } from '~/utils/i18n'
import { ciTrash } from '~/icons/source/solid/trash'
import { ciPlus } from '~/icons/source/solid/plus'

export default defineComponent({
  components: { FieldError },
  props: {
    downpayment: {
      type: [Number, String],
      default: null
    },
    mileage: {
      type: Number,
      default: null
    },
    duration: {
      type: Number,
      default: null
    },
    lease: {
      type: [Number, String],
      default: null
    },
    purchasePrice: {
      type: [Number, String],
      default: null
    },
    specs: {
      type: Object,
      required: true
    },
    error: {
      type: Object,
      default: null
    },
    name: {
      type: String,
      required: true
    },
    index: {
      type: Number,
      required: true
    },
    canAdd: {
      type: Boolean,
      required: false,
      default: false
    },
    lastRowIsNotFilled: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  setup(props, { emit }) {
    const { error, specs, name, lastRowIsNotFilled, index } = toRefs(props)

    const formValidationBuilder = useDep(FormValidationBuilder)
    const { addClientValidator } = useClassifiedForm()
    const { t } = useI18n()

    const leaseError = ref(error.value?.lease)
    const downpaymentError = ref(error.value?.downpayment)
    const mileageError = ref(error.value?.mileage)
    const durationError = ref(error.value?.duration)
    // eslint-disable-next-line camelcase
    const purchasePriceError = ref(error.value?.purchase_price)

    const durationOptions = computed(() => {
      return specs.value.duration.values
    })
    const mileageOptions = computed(() => {
      return specs.value.mileage.values
    })
    const purchasePriceIsValid = computed(() => {
      return computeFieldState(purchasePriceError.value)
    })
    const leasingIsValid = computed(() => {
      return computeFieldState(leaseError.value)
    })
    const downpaymentIsValid = computed(() => {
      return computeFieldState(downpaymentError.value)
    })

    watch(error, val => {
      leaseError.value = val?.lease
      downpaymentError.value = val?.downpayment
      mileageError.value = val?.mileage
      durationError.value = val?.duration
      // eslint-disable-next-line camelcase
      purchasePriceError.value = val?.purchase_price
    })

    const isRequired = (spec: any) => {
      return Boolean(spec?.required)
    }

    const addOptionButtonTooltipText = computed(() => {
      if (lastRowIsNotFilled.value) {
        return index.value === 0
          ? t('add_new_without_first')
          : t('add_new_without_last')
      }
      return ''
    })

    const validateAll = ({
      downpayment = props.downpayment,
      lease = props.lease,
      mileage = props.mileage,
      duration = props.duration,
      purchasePrice = props.purchasePrice
    }) => {
      const {
        downpayment: downpaymentSpecs,
        lease: leaseSpecs,
        mileage: mileageSpecs,
        duration: durationSpecs,
        purchase_price: purchasePriceSpecs
      } = specs.value

      downpaymentError.value = formValidationBuilder
        .new()
        .required(downpaymentSpecs.required, downpayment)
        .isNumeric(downpayment)
        .aboveMinNumber(downpaymentSpecs.min, downpayment)
        .validate()
      leaseError.value = formValidationBuilder
        .new()
        .required(leaseSpecs.required, lease)
        .isNumeric(lease)
        .aboveMinNumber(leaseSpecs.min, lease)
        .belowMaxNumber(leaseSpecs.max, lease)
        .validate()
      mileageError.value = formValidationBuilder
        .new()
        .required(mileageSpecs.required, mileage)
        .validate()
      durationError.value = formValidationBuilder
        .new()
        .required(durationSpecs.required, duration)
        .validate()

      purchasePriceError.value = formValidationBuilder
        .new()
        .required(purchasePriceSpecs.required, purchasePrice)
        .isNumeric(purchasePrice)
        .validate()

      return !(
        leaseError.value ||
        downpaymentError.value ||
        mileageError.value ||
        durationError.value ||
        purchasePriceError.value
      )
    }

    onMounted(() => {
      if (props.index > 0) {
        addClientValidator({
          key: name.value,
          validator: () =>
            validateAll({
              downpayment: props.downpayment,
              lease: props.lease,
              mileage: props.mileage,
              duration: props.duration,
              purchasePrice: props.purchasePrice
            })
        })
      }
    })
    const handleDownpaymentChange = (downpayment: string) => {
      validateAll({ downpayment })
      emit('downpayment-change', downpayment)
    }
    const handleLeaseChange = (lease: string) => {
      validateAll({ lease })
      emit('lease-change', lease)
    }
    const handleDurationChange = (duration: number) => {
      validateAll({ duration })
      emit('duration-change', duration)
    }
    const handleMileageChange = (mileage: number) => {
      validateAll({ mileage })
      emit('mileage-change', mileage)
    }
    const handlePurchasePriceChange = (purchasePrice: string) => {
      validateAll({ purchasePrice })
      emit('purchase-price-change', purchasePrice)
    }

    const icons = {
      delete: ciTrash,
      plus: ciPlus
    }

    return {
      leaseError,
      downpaymentError,
      mileageError,
      durationError,
      purchasePriceError,
      durationOptions,
      mileageOptions,
      purchasePriceIsValid,
      leasingIsValid,
      downpaymentIsValid,
      isRequired,
      validateAll,
      handleDownpaymentChange,
      handleLeaseChange,
      handleDurationChange,
      handleMileageChange,
      handlePurchasePriceChange,
      icons,
      addOptionButtonTooltipText,
      t
    }
  },
  i18n: defineComponentTranslations({
    add_new_without_last: {
      en: 'Fill the previous option to add more options',
      el: 'Συμπλήρωσε πρώτα την προηγούμενη επιλογή πριν προσθέσεις μια νέα'
    },
    add_new_without_first: {
      en: 'Fill this option before trying to add a new one',
      el: 'Συμπλήρωσε πρώτα αυτή την επιλογή πριν προσθέσεις μια νέα'
    }
  })
})
